import type { Dispatch } from 'redux';

import { MEDIA_TYPE, VIDEO_TYPE } from '../base/media/constants';
import logger from '../base/media/logger';
import { trackRemoved } from '../base/tracks/actions';
import { toggleScreensharing } from '../base/tracks/mhActions';
import { setScreenshareAudioTrack } from '../screen-share/actions';
import { isAudioOnlySharing, isScreenVideoShared } from '../screen-share/functions';

export * from './functions.any';


/**
 * Stops screen sharing when moving to a breakout room.
 * Ensures the action completes before room transition to prevent UI state desync
 * and ensures browser's native screenshare indicator bar is closed.
 *
 * @param {Dispatch} dispatch - Redux dispatch function
 * @returns {Promise<boolean>} Resolves to true when screen sharing is completely stopped
 */
export async function stopScreenSharingWhileMovingBreakoutRoom(dispatch: Dispatch): Promise<boolean> {
    try {
        await dispatch(async (thunkDispatch: any, getState: Function) => {
            const state = getState();

            // 1. Collect all local desktop/screenshare tracks
            const tracks = state['features/base/tracks'] || [];
            const desktopTracks = tracks.filter((t: any) => t.local && (
                t.mediaType === MEDIA_TYPE.SCREENSHARE
                || t.videoType === VIDEO_TYPE.DESKTOP
                || t.mhTrack?.getVideoType?.() === VIDEO_TYPE.DESKTOP
            ));

            // 2. Explicitly stop all native MediaStreamTracks so the browser's native
            // "sharing your screen" indicator bar immediately closes.
            desktopTracks.forEach((t: any) => {
                const mhTrack = t.mhTrack;

                if (mhTrack) {
                    try {
                        if (mhTrack.track && typeof mhTrack.track.stop === 'function') {
                            mhTrack.track.stop();
                        }
                        if (mhTrack.stream && typeof mhTrack.stream.getTracks === 'function') {
                            mhTrack.stream.getTracks().forEach((track: any) => {
                                if (track && typeof track.stop === 'function') {
                                    track.stop();
                                }
                            });
                        }
                        if (typeof mhTrack.getOriginalStream === 'function') {
                            const origStream = mhTrack.getOriginalStream();

                            if (origStream && typeof origStream.getTracks === 'function') {
                                origStream.getTracks().forEach((track: any) => {
                                    if (track && typeof track.stop === 'function') {
                                        track.stop();
                                    }
                                });
                            }
                        }
                        if (typeof mhTrack.stopStream === 'function') {
                            mhTrack.stopStream();
                        }
                    } catch (err) {
                        logger.warn('Error stopping desktop MediaStreamTrack:', err);
                    }
                }
            });

            // 3. Stop desktop audio track if active
            const { desktopAudioTrack } = state['features/screen-share'] || {};

            if (desktopAudioTrack) {
                try {
                    if (desktopAudioTrack.track && typeof desktopAudioTrack.track.stop === 'function') {
                        desktopAudioTrack.track.stop();
                    }
                    if (desktopAudioTrack.stream && typeof desktopAudioTrack.stream.getTracks === 'function') {
                        desktopAudioTrack.stream.getTracks().forEach((track: any) => {
                            if (track && typeof track.stop === 'function') {
                                track.stop();
                            }
                        });
                    }
                    if (typeof desktopAudioTrack.getOriginalStream === 'function') {
                        const origStream = desktopAudioTrack.getOriginalStream();

                        if (origStream && typeof origStream.getTracks === 'function') {
                            origStream.getTracks().forEach((track: any) => {
                                if (track && typeof track.stop === 'function') {
                                    track.stop();
                                }
                            });
                        }
                    }
                    if (typeof desktopAudioTrack.stopStream === 'function') {
                        desktopAudioTrack.stopStream();
                    }
                } catch (err) {
                    logger.warn('Error stopping desktop audio MediaStreamTrack:', err);
                }
            }

            // 4. Update screensharing Redux state & notify external API
            const promises = [];

            if (isAudioOnlySharing(state)) {
                promises.push(thunkDispatch(toggleScreensharing(false, true)));
            }

            if (isScreenVideoShared(state)) {
                promises.push(thunkDispatch(toggleScreensharing(false, false)));
            }

            await Promise.all(promises);

            // 5. Remove desktop tracks from Redux store and dispose them safely
            for (const t of desktopTracks) {
                if (t.mhTrack) {
                    thunkDispatch(trackRemoved(t.mhTrack));
                    try {
                        await Promise.race([
                            t.mhTrack.dispose(),
                            new Promise(resolve => setTimeout(resolve, 500))
                        ]);
                    } catch (e) {
                        logger.warn('Error disposing desktop mhTrack:', e);
                    }
                }
            }

            if (desktopAudioTrack) {
                try {
                    await Promise.race([
                        desktopAudioTrack.dispose(),
                        new Promise(resolve => setTimeout(resolve, 500))
                    ]);
                } catch (e) {
                    logger.warn('Error disposing desktopAudioTrack:', e);
                }
                thunkDispatch(setScreenshareAudioTrack(null));
            }
        });

        return true;
    } catch (error) {
        // If stopping screen sharing throws, log it but don't prevent the room transition
        logger.error('Failed to stop screen sharing during breakout room transition:', error);

        return true;
    }
}


